import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {CampaignForm} from '@app/@core/advertising/campaign/campaign-form';
import {CampaignDraft} from "@app/@core/advertising/campaign/campaign-draft";
import {ConfigService} from "@app/@core/@config/config.service";
import {FormGroup} from "@angular/forms";
import {COUNTRIES, Country} from '@app/@core/@models/common/country.model';
import {I18nLangService} from '@app/@i18n/services/i18n-lang.service';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-campaign-country[campaignForm]',
  templateUrl: './campaign-country.component.html',
  styleUrls: ['./campaign-country.component.scss']
})
export class CampaignCountryComponent implements OnInit, OnChanges {
  @Input() campaignForm!: FormGroup;
  countries: Country[] = [];
  alphabetizedCountries: Country[] = [];

  constructor(
    public configService: ConfigService,
    private i18nLangService: I18nLangService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.sortCountriesByPriority();
    this.setDefaultCountry();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['campaignForm'] && this.campaignForm) {
      this.sortCountriesByPriority();
      this.setDefaultCountry();
    }
  }

  getTranslatedCountryName(countryCode: string): string {
    const currentLang = this.i18nLangService.getLanguage();
    if (currentLang === 'fr') {
      return this.translateService.instant(`country.${countryCode}`);
    }
    return COUNTRIES.find(c => c.code === countryCode)?.name || countryCode;
  }

  private sortCountriesByPriority(): void {
    const networkData = this.configService.getNetworkData();
    const priorityCountries = networkData?.advertiser?.country?.prio || [];

    // Create a map for quick priority lookup
    const priorityMap = new Map<string, number>();
    priorityCountries.forEach((code, index) => {
      priorityMap.set(code, index);
    });

    // Separate priority countries from others
    const priorityCountriesList: Country[] = [];
    const otherCountries: Country[] = [];

    COUNTRIES.forEach(country => {
      if (priorityMap.has(country.code)) {
        priorityCountriesList.push(country);
      } else {
        otherCountries.push(country);
      }
    });

    // Sort priority countries by their priority order
    priorityCountriesList.sort((a, b) => {
      const aPriority = priorityMap.get(a.code)!;
      const bPriority = priorityMap.get(b.code)!;
      return aPriority - bPriority;
    });

    // Sort other countries alphabetically by translated name
    const currentLang = this.i18nLangService.getLanguage();
    if (currentLang === 'fr') {
      otherCountries.sort((a, b) => {
        const aName = this.translateService.instant(`country.${a.code}`);
        const bName = this.translateService.instant(`country.${b.code}`);
        return aName.localeCompare(bName, 'fr');
      });
    } else {
      otherCountries.sort((a, b) => a.name.localeCompare(b.name));
    }

    this.countries = priorityCountriesList;
    this.alphabetizedCountries = otherCountries;
  }

  private setDefaultCountry(): void {
    const countryControl = this.campaignForm.get('country');
    if (countryControl && this.countries.length > 0) {
      const currentValue = countryControl.value;

      // Check if the current value is empty, null, or not in our countries list
      const isValidValue = currentValue &&
        (this.countries.some(c => c.code === currentValue) ||
         this.alphabetizedCountries.some(c => c.code === currentValue));

      if (!isValidValue) {
        // Set default to the first priority country
        countryControl.setValue(this.countries[0].code);
      }
    }
  }
}
